import { BlockResult, IResultFormat, IResultTypes, ResultStatus } from 'types';
import CustomChart from 'components/chart/CustomChart';
import Typography from 'components/shared/Typography';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { cleanString } from 'utils';
import { Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useFetchBlockResult } from 'services';
import classNames from 'classnames';
import BlockQueryError from 'components/blocks/BlockQueryError';
import Table from 'components/blocks/Table';

interface BlockitemInterface {
  results: BlockResult[];
  result_status?: string;
  resultType?: IResultTypes;
  result_format?: string;
  isEditable: boolean;
  reportId: string;
  blockId: string;
  title: string;
}

export default function PdfBlockItem(res: BlockitemInterface) {
  const { result_status, reportId, blockId, results, title } = res;

  const [regenerateComment, setRegenerateComment] = useState('');
  const [isRedoing, setIsRedoing] = useState<boolean>(false);

  const timeUpdated = (results && results.length > 0 && results[0].updated) || new Date();

  const date = new Date(timeUpdated as string).toLocaleDateString();
  const time = new Date(timeUpdated as string).toLocaleTimeString();

  const {
    data: blockResult,
    isFetching: isFetchingBlock,
    error: isBlockError,
    isSuccess: isBlockSuccess,
    refetch,
  } = useFetchBlockResult(reportId, blockId, Boolean(results.length > 0), isRedoing, regenerateComment);

  useEffect(() => {
    if (results.length === 0) {
      refetch();
    }
  }, [results]);

  const block_result_status = blockResult?.data?.block?.result_status;

  const computedResults = (blockResult && isBlockSuccess && blockResult?.data?.results) || results;

  const displayBlocks = computedResults && computedResults?.length > 0 && result_status !== 'error';

  return (
    <div className='flex w-full'>
      <div className='w-full'>
        <div className=''>
          <div className={classNames('text-left mb-2')}>
            <Typography variant='displaysm' weight='semibold' className='text-shade-black'>
              {title}
            </Typography>
          </div>
          
        </div>
        {(result_status === ResultStatus.ERROR || isBlockError || block_result_status === ResultStatus.ERROR) && !isFetchingBlock && (
          <BlockQueryError reportId={reportId} blockId={blockId} message={computedResults[0].content as string} />
        )}
        {isFetchingBlock && (
          <div className='w-full flex justify-center items-center relative bg-grey-50 mt-20 animate-bounce rounded-lg'>
            <div className='spinner-box'>
              <div className='configure-border-1'>
                <div className='configure-core'></div>
              </div>
              <div className='configure-border-2'>
                <div className='configure-core'></div>
              </div>
            </div>
          </div>
        )}
        {result_status !== ResultStatus.ERROR && block_result_status !== ResultStatus.ERROR && !isFetchingBlock && !isBlockError && (
          <>
            <div>
              {displayBlocks &&
                computedResults.map((result: any, index: number) => (
                  <div key={index}>
                    <div className={classNames('w-full mt-4')}>
                      <div className={classNames(' text-justify')}>
                        {result.result_type === IResultTypes.TEXT && result.result_format !== IResultFormat.TABLE && (
                          <>
                            <Text whiteSpace={'pre-line'} borderWidth={'0px'} id={String(blockId)} className={result.id}>
                              {' '}
                              <MarkdownPreview
                                source={`${result.content}`}
                                wrapperElement={{
                                  'data-color-mode': 'light',
                                }}
                              />
                            </Text>
                          </>
                        )}
                      </div>

                      <div className={classNames('w-full flex justify-center')}>
                        {result.result_format === IResultFormat.CHART && result.result_type === IResultTypes.JSON_STRING && result.content && (
                          <>
                            <div className={classNames('border-[1px] border-grey-100 rounded-md p-4 w-full flex justify-center max-w-[700px]')}>
                              <CustomChart
                                chartData={JSON.parse(result.content as string)}
                                chartType={result.chart_type || 'bar_chart'}
                                title={title as string}
                              />
                            </div>
                          </>
                        )}
                      </div>

                      {result.result_format === IResultFormat.TABLE && (
                        <>
                          <Table data={cleanString(result.content) as any} />
                        </>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
